import { ReactElement } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { BackrsCard } from '@backrs_ui/common-ui/src/atoms/BackrsCard';
import { BackrsIcons } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';

import { Theme, Box, SxProps, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { NuggetArticle } from './NuggetArticle';
import { NuggetAudio } from './NuggetAudio';
import { NuggetDocument } from './NuggetDocument';
import { NuggetEvent } from './NuggetEvent';
import { NuggetInstitution } from './NuggetInstitution';
import { NuggetInternship } from './NuggetInternship/NuggetInternship';
import { NuggetOpportunity } from './NuggetOpportunity';
import { NuggetPost } from './NuggetPost';
import { NuggetProfile } from './NuggetProfile';
import { NuggetResource } from './NuggetResource';
import { NuggetScholarship } from './NuggetScholarship/NuggetScholarship';
import { NuggetTextResponse } from './NuggetTextResponse';
import { NuggetUXVariant } from './NuggetUITypes';
import { NuggetVideo } from './NuggetVideo';
import { PanelNuggetHeader } from './PanelNuggetHeader';

function variantMapper(
  variant: NuggetUXVariant,
  contentSize: 'SMALL' | 'LARGE',
): {
  component: ReactElement;
  icon: BackrsIcons;
} {
  switch (variant.type) {
    case 'opportunity':
      return {
        component: <NuggetOpportunity variant={{ type: 'SMALL', deadline: variant.deadline }} />,
        icon: 'IconOpportunity',
      };

    case 'event':
      return {
        component: <NuggetEvent variant={{ type: 'SMALL', date: variant.date }} />,
        icon: 'IconEvent',
      };

    case 'video-external':
      return {
        component: (
          <NuggetVideo
            variant={{
              type: 'SMALL',
              url: variant.url,
              previewImage: variant.previewImage,
              isInternal: false,
            }}
          />
        ),
        icon: 'IconVideo',
      };

    case 'video-internal':
      return {
        component: (
          <NuggetVideo
            variant={{
              type: 'SMALL',
              url: variant.url,
              previewImage: variant.previewImage,
              isInternal: true,
            }}
          />
        ),
        icon: 'IconVideo',
      };

    case 'article':
      return {
        component: (
          <NuggetArticle
            variant={{
              type: 'SMALL',
              title: variant.title,
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconArticle',
      };

    case 'institution':
      return {
        component: (
          <NuggetInstitution
            variant={{
              type: 'SMALL',
              previewImage: variant.previewImage,
            }}
          />
        ),
        icon: 'IconInstitution',
      };

    case 'audio':
      return {
        component: <NuggetAudio variant={{ type: 'SMALL' }} />,
        icon: 'IconAudio',
      };

    case 'document':
      return {
        component: (
          <NuggetDocument
            variant={{
              type: 'SMALL',
              title: variant.title,
            }}
          />
        ),
        icon: 'IconDocument',
      };

    case 'resource':
      return {
        component: (
          <NuggetResource
            variant={{ type: 'SMALL', previewImage: variant.previewImage, title: variant.title }}
          />
        ),
        icon: 'IconArticle',
      };

    case 'text-response':
      return {
        component: (
          <NuggetTextResponse
            variant={{
              type: 'SMALL',
              additionalQuote: variant.additionalQuote,
              additionalQuoteAuthor: variant.additionalQuoteAuthor,
              contentSize,
            }}
          />
        ),
        icon: 'IconTextResponse',
      };

    case 'post':
      return {
        component: (
          <NuggetPost
            variant={{
              type: 'SMALL',
              additionalQuote: variant.additionalQuote,
              additionalQuoteAuthor: variant.additionalQuoteAuthor,
              contentSize,
            }}
          />
        ),
        icon: 'IconTextResponse',
      };

    case 'book':
      return {
        component: (
          <NuggetResource
            variant={{ type: 'SMALL', title: variant.title, previewImage: variant.previewImage }}
          />
        ),
        icon: 'IconBook',
      };

    case 'competency':
      return {
        component: <></>,
        icon: 'ProsperLogo',
      };

    case 'scholarships':
      return {
        component: (
          <NuggetScholarship
            contentSize={contentSize}
            variant={{
              type: 'SMALL',
              awardMoneyRangeMin: variant.scholarshipInfo.award_money_range_min,
              awardMoneyRangeMax: variant.scholarshipInfo.award_money_range_max,
              awardType: variant.scholarshipInfo.award_type,
              awardCategory: variant.scholarshipInfo.award_category,
              awardDeadline: variant.scholarshipInfo.submission_deadline,
            }}
          />
        ),
        icon: 'IconScholarship',
      };

    case 'internship':
      return {
        component: (
          <NuggetInternship
            variant={{ type: 'SMALL', internshipInfo: variant.internshipInfo }}
            contentSize={contentSize}
          />
        ),
        icon: 'IconBriefcase',
      };

    case 'profile-external':
      return {
        component: (
          <NuggetProfile
            variant={{
              type: 'SMALL',
              abbreviatedName: variant.abbreviatedName,
              location: variant.location,
              jobTitle: variant.jobTitle,
              employer: variant.employer,
              school: variant.school,
              profileImageUrl: variant.profileImageUrl,
              profileType: 'EXTERNAL',
              contentSize,
            }}
          />
        ),
        icon: 'IconProfileExternal',
      };

    case 'profile-internal':
      return {
        component: (
          <NuggetProfile
            variant={{
              type: 'SMALL',
              abbreviatedName: variant.abbreviatedName,
              location: variant.location,
              jobTitle: variant.jobTitle,
              employer: variant.employer,
              school: variant.school,
              role: variant.role,
              profileImageUrl: variant.profileImageUrl,
              profileType: 'INTERNAL',
              contentSize,
            }}
          />
        ),
        icon: 'IconProfileInternal',
      };

    case 'career-path':
      return {
        component: <></>,
        icon: 'IconBriefcase',
      };

    default:
      return {
        component: <></>,
        icon: 'ProsperLogo',
      };
  }
}

type PanelNuggetProps = Readonly<{
  title: string;
  subTitle?: string;
  body?: string;
  variant: NuggetUXVariant;
  onClick?: () => void;
  subText?: string;
  size?: 'SMALL' | 'LARGE';
  useSplitLayout?: boolean;
}>;

export const PanelNugget = ({
  title,
  subTitle,
  body,
  variant,
  onClick,
  subText,
  size = 'LARGE',
  useSplitLayout = false,
}: PanelNuggetProps) => {
  const { component, icon } = variantMapper(variant, size);

  return (
    <BackrsCard
      onClick={onClick}
      sx={size === 'SMALL' ? smallContentSizeContainerStyle : largeContentSizeContainerStyle}
    >
      <Box sx={contentContainerStyle}>
        <PanelNuggetHeader iconName={icon} title={title} size={size} subTitle={subTitle} />
        <Box sx={{ ...layoutContainerStyle, flexDirection: useSplitLayout ? 'row' : 'column' }}>
          <Box sx={{ flex: 1 }}>
            {body && (
              <Typography variant="body1" sx={bodyTextStyle}>
                {body.replace(/--/g, '—')}
              </Typography>
            )}
            {component}
          </Box>
          {subText && (
            <Typography
              variant="body1"
              sx={useSplitLayout ? { ...subTextStyle, width: '50%' } : subTextStyle}
            >
              <ReactMarkdown
                components={{
                  p: ({ ...props }) => <p style={{ margin: '0px' }} {...props} />,
                }}
              >
                {subText}
              </ReactMarkdown>
            </Typography>
          )}
        </Box>
      </Box>
    </BackrsCard>
  );
};

const layoutContainerStyle: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
  width: '100%',
};

const contentContainerStyle: SxProps<Theme> = {
  justifyContent: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const subTextStyle: SxProps<Theme> = {
  backgroundColor: appColors.color.blue10,
  borderRadius: 2,
  fontSize: '10pt',
  padding: 1.5,
  width: '100%',
  boxSizing: 'border-box',
};

const smallContentSizeContainerStyle: SxProps<Theme> = {
  paddingX: 2,
  paddingY: 1,
  border: `1px solid ${appColors.neutral.black}`,
};

const largeContentSizeContainerStyle: SxProps<Theme> = {
  border: `1px solid ${appColors.neutral.black}`,
  height: '100%',
};

const bodyTextStyle: SxProps<Theme> = {
  fontSize: '10.5pt',
  lineHeight: '1.4',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 5,
  textOverflow: 'ellipsis',
};
